// The unified pipeline status vocabulary — the single source of truth shared by
// the unified LISTING and the standard/edge DETAIL headers. It lives here (not in
// the list feature) so shared components (EdgeGenericHeader, the detail header)
// can consume the status chips without reaching into `pipeline-list/unified`.
import { GenericStatus, GenericStatusColorType } from '@hevoio/hevo-ui-toolkit';

/**
 * The unified display vocabulary. Some values share a literal with the edge
 * `IntegrationStatus` enum, but this is a deliberately coarser, UI-owned set —
 * do not treat the two as interchangeable (e.g. as the same Record key type).
 */
export enum UnifiedPipelineStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  DISABLING = 'DISABLING',
  ENABLING = 'ENABLING',
  DELETING = 'DELETING',
  DELETED = 'DELETED'
}

export const UNIFIED_STATUS_DATA_MAP: {
  [key in UnifiedPipelineStatus]: GenericStatus<UnifiedPipelineStatus>;
} = {
  [UnifiedPipelineStatus.DRAFT]: {
    value: UnifiedPipelineStatus.DRAFT,
    displayName: 'Draft',
    colorType: GenericStatusColorType.DEFAULT
  },
  [UnifiedPipelineStatus.ACTIVE]: {
    value: UnifiedPipelineStatus.ACTIVE,
    displayName: 'Active',
    colorType: GenericStatusColorType.SUCCESS
  },
  [UnifiedPipelineStatus.DISABLED]: {
    value: UnifiedPipelineStatus.DISABLED,
    displayName: 'Disabled',
    colorType: GenericStatusColorType.DISABLED
  },
  [UnifiedPipelineStatus.FAILED]: {
    value: UnifiedPipelineStatus.FAILED,
    displayName: 'Failed',
    colorType: GenericStatusColorType.ERROR
  },
  [UnifiedPipelineStatus.IN_PROGRESS]: {
    value: UnifiedPipelineStatus.IN_PROGRESS,
    displayName: 'In Progress',
    colorType: GenericStatusColorType.INFO
  },
  [UnifiedPipelineStatus.DISABLING]: {
    value: UnifiedPipelineStatus.DISABLING,
    displayName: 'Disabling',
    colorType: GenericStatusColorType.INFO
  },
  [UnifiedPipelineStatus.ENABLING]: {
    value: UnifiedPipelineStatus.ENABLING,
    displayName: 'Enabling',
    colorType: GenericStatusColorType.INFO
  },
  [UnifiedPipelineStatus.DELETING]: {
    value: UnifiedPipelineStatus.DELETING,
    displayName: 'Deleting',
    colorType: GenericStatusColorType.INFO
  },
  [UnifiedPipelineStatus.DELETED]: {
    value: UnifiedPipelineStatus.DELETED,
    displayName: 'Deleted',
    colorType: GenericStatusColorType.DISABLED
  }
};

// Standard raw pipeline_status.status → unified status.
export const STANDARD_STATUS_MAP: Record<string, UnifiedPipelineStatus> = {
  DRAFT: UnifiedPipelineStatus.DRAFT,
  OK: UnifiedPipelineStatus.ACTIVE,
  SOURCE_RATE_LIMIT_BREACHED: UnifiedPipelineStatus.ACTIVE,
  PAUSED: UnifiedPipelineStatus.DISABLED,
  FAILED: UnifiedPipelineStatus.FAILED,
  FAILED_PERMANENTLY: UnifiedPipelineStatus.FAILED
};

// Edge raw status.type → unified status. Missing keys fall back to IN_PROGRESS.
// The listing endpoint excludes draft rows; the CREATED*/CONFIGURED* mappings
// below are defensive so a stray draft renders as DRAFT, not In Progress.
export const EDGE_STATUS_MAP: Record<string, UnifiedPipelineStatus> = {
  STREAMING: UnifiedPipelineStatus.ACTIVE,
  INITIALIZED: UnifiedPipelineStatus.ACTIVE,
  PAUSED: UnifiedPipelineStatus.DISABLED,
  DISABLED: UnifiedPipelineStatus.DISABLED,
  FAILED: UnifiedPipelineStatus.FAILED,
  DISABLE_FAILED: UnifiedPipelineStatus.FAILED,
  ENABLE_FAILED: UnifiedPipelineStatus.FAILED,
  SYNC_TASK_FLOW_FAILED: UnifiedPipelineStatus.FAILED,
  DELETE_FAILED: UnifiedPipelineStatus.FAILED,
  INITIALIZE_FAILED: UnifiedPipelineStatus.FAILED,
  RESTART_FAILED: UnifiedPipelineStatus.FAILED,
  INITIALIZING: UnifiedPipelineStatus.IN_PROGRESS,
  RESTARTING: UnifiedPipelineStatus.IN_PROGRESS,
  // Own display statuses (chip vocabulary mirrors the filter keys 1:1, so the
  // column and the status filter are exact inverses):
  ENABLING: UnifiedPipelineStatus.ENABLING,
  DISABLING: UnifiedPipelineStatus.DISABLING,
  DELETING: UnifiedPipelineStatus.DELETING,
  DELETED: UnifiedPipelineStatus.DELETED,
  CREATED: UnifiedPipelineStatus.DRAFT,
  CREATED_SOURCE: UnifiedPipelineStatus.DRAFT,
  CREATED_CATALOG: UnifiedPipelineStatus.DRAFT,
  CREATED_DESTINATION: UnifiedPipelineStatus.DRAFT,
  CONFIGURED_OBJECT: UnifiedPipelineStatus.DRAFT,
  CONFIGURED_PIPELINE: UnifiedPipelineStatus.DRAFT
};

// Detail-page header chips: under the unified view the standard and edge detail
// headers show the SAME 9-status vocabulary as the listing, mapped from the raw
// engine status enum (never the per-engine display_name the backends send).
export function unifiedStandardStatusChip(rawStatus?: string): GenericStatus<UnifiedPipelineStatus> {
  return UNIFIED_STATUS_DATA_MAP[STANDARD_STATUS_MAP[rawStatus ?? ''] ?? UnifiedPipelineStatus.IN_PROGRESS];
}

export function unifiedEdgeStatusChip(rawType?: string): GenericStatus<UnifiedPipelineStatus> {
  return UNIFIED_STATUS_DATA_MAP[EDGE_STATUS_MAP[rawType ?? ''] ?? UnifiedPipelineStatus.IN_PROGRESS];
}
